import { css } from '@emotion/react'
import { memo, useRef } from 'react'
import { MYML_LOCATIONS } from '../../../../mfcLogic/api/modelsYouMayLike'
import { idToSlug } from '../../../../state/state.users'
import useModelsYouMayLike from '../../../Hooks/useModelsYouMayLike'
import { usePortalStateSelector } from '../../../Hooks/usePortalState'
import { useRoomSelector } from '../../../Hooks/useRoom'
import { useScreenStateSelector } from '../../../Hooks/useScreenState'
import { getThemeSpacing } from '../../../styles/appTheme'
import { palette } from '../../../styles/colors'
import layers from '../../../styles/layers'
import shape from '../../../styles/shape'
import { InlineCircularLoading } from '../../InlineLoading'
import LoungeChatPreview from '../../PreviewPosterImage/LoungeChatPreview'
import { getDefaultDivision } from '../common/utilities'

const splitContainer = css({
    height: '100%',
    display: 'flex',
    flexFlow: 'column wrap',
    background: palette.backgroundRaised,
    '& > div:first-of-type': {
        borderRight: `1px solid ${palette.backgroundLightGray}`,
    },
})

const darkModeBackground = css({
    background:
        'radial-gradient(var(--avatar-badge-hover), var(--background-raised))',
})

const splitSection = css({
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100%',
    scrollBehavior: 'smooth',
    width: '50%',
    position: 'relative',
    '& span': {
        fontSize: '0.625rem',
        opacity: 0.66,
    },
})

const sectionHeaderStyles = css({
    fontSize: '0.625rem',
    height: 'auto',
    paddingTop: getThemeSpacing(1),
    paddingBottom: getThemeSpacing(1),
    textAlign: 'center',
    marginBottom: getThemeSpacing(1),
})

const previewStyles = css({
    padding: getThemeSpacing(1),
    transition: 'all 0.1s ease-in',
    flex: '0 0',
    // alignItems: 'center',
    // justifyContent: 'center',
    display: 'flex',
    flexFlow: 'column nowrap',
})

const previewWrapperStyles = css({
    border: palette.layerLightBorder,
    borderRadius: shape.borderRadius,
})
const highlightPreview = css({
    border: '1px solid ' + palette.highlightActiveRoom,
})

const senderContainerStyles = css({
    fontSize: 11,
    // paddingLeft: getThemeSpacing(0.5),
    // paddingRight: getThemeSpacing(0.5),
    width: '100%',
    opacity: 0.67,
})

const sharedByStyles = css({
    width: '100%',
    textAlign: 'center',
    background: palette.backgroundLightGray,
    // border: '1px solid',
    borderBottomRightRadius: shape.borderRadius,
    borderBottomLeftRadius: shape.borderRadius,
    // borderColor: 'rgba(0, 0, 0, 0.15)',
    boxShadow: layers.one, // '0px 1px 3px 0px rgba(0, 0, 0, 0.25)',
})

const commentStyles = css({
    fontStyle: 'italic',
    fontWeight: 'bold',
})

const loadingRootStyles = css({
    alignItems: 'center',
    justifyContent: 'center',
    flexFlow: 'column nowrap',
    display: 'flex',
    margin: getThemeSpacing(2),
    width: '100%',
    flex: '1 1 200px',
})

interface LoungePreviewsProps {
    id: number
    darkMode: boolean
    fullWindow: boolean
    loungeSlug: string
    surrogateID?: number
}

const getMymlLoungePreviews = (
    modelResults,
    {
        loungeSlug,
        loading,
        fullWindow,
        darkMode,
        split = false,
        limit = 5,
        size = 300,
    },
) =>
    !loading ? (
        modelResults.slice(0, limit).map((preview, previewIndex) => {
            return (
                <div
                    key={preview.user_id + '-' + previewIndex}
                    css={previewStyles}
                >
                    <div css={previewWrapperStyles}>
                        <LoungeChatPreview
                            modelId={preview.user_id}
                            loungeSlug={loungeSlug}
                            myml
                            width={size}
                            fullWindow={fullWindow}
                            darkMode={darkMode}
                            large
                        />
                    </div>
                </div>
            )
        })
    ) : (
        <div
            css={split ? loadingRootStyles : previewStyles}
            style={{ minWidth: size, margin: getThemeSpacing(2) }}
        >
            <InlineCircularLoading />
        </div>
    )

const getPreviewLoungePreviews = (
    previews,
    { surrogateID, loungeSlug, fullWindow, darkMode, size = 300 },
) =>
    previews.map((preview, previewIndex) => {
        return (
            <div
                key={preview.timestamp + '-' + previewIndex}
                css={previewStyles}
            >
                <div
                    css={[
                        previewWrapperStyles,
                        surrogateID === preview.id && highlightPreview,
                    ]}
                >
                    <LoungeChatPreview
                        modelId={preview.id}
                        loungeSlug={loungeSlug}
                        width={size}
                        fullWindow={fullWindow}
                        darkMode={darkMode}
                        large
                    />
                    <div style={{ maxWidth: size }} css={senderContainerStyles}>
                        <div css={sharedByStyles}>
                            <span>Shared by {idToSlug[preview.sender]}</span>
                            {preview.message && (
                                <span css={commentStyles}>
                                    {' - "' + preview.message + '"'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    })

const LoungePreviews = memo((props: LoungePreviewsProps) => {
    const { id, darkMode, fullWindow, loungeSlug, surrogateID } = props
    const previews = useRoomSelector(id, 'loungePreviews')
    const roomWidth = useScreenStateSelector('roomWidth', 0)
    const roomHeight = useScreenStateSelector('roomHeight', 0)
    const roomDivision =
        usePortalStateSelector('division') ||
        getDefaultDivision(roomWidth, roomHeight)
    const usableWidth =
        roomHeight > roomWidth
            ? roomWidth
            : roomWidth * (1 - roomDivision / 100)
    const containerRef = useRef(null) // to get the height for large lounge check
    const { modelResults, loading } = useModelsYouMayLike(
        surrogateID,
        MYML_LOCATIONS.recommendationPage,
        true,
    )

    const previewWidth = Math.max(usableWidth / 2 - 24, 130)

    return (
        <div
            css={[splitContainer, darkMode && darkModeBackground]}
            ref={containerRef}
        >
            <div className="no-scrollbars" css={splitSection}>
                <div
                    css={sectionHeaderStyles}
                    style={{ width: usableWidth / 2 }}
                >
                    Recently Shared Models
                </div>
                {getPreviewLoungePreviews(previews, {
                    surrogateID,
                    loungeSlug,
                    fullWindow,
                    darkMode,
                    size: previewWidth,
                })}
            </div>
            <div className="no-scrollbars" css={splitSection}>
                <div
                    css={sectionHeaderStyles}
                    style={{ width: usableWidth / 2 }}
                >
                    Models You May Like
                </div>
                {getMymlLoungePreviews(modelResults, {
                    limit: 8,
                    loungeSlug,
                    split: true,
                    loading,
                    fullWindow,
                    darkMode,
                    size: previewWidth,
                })}
            </div>
        </div>
    )
})

export default LoungePreviews
